import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AccountBalance as BankIcon,
  CreditCard as EquipmentIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  Settings as AttributesIcon,
  // Visibility,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import PageLayout from "../components/layout/PageLayout";
import {
  useLazyGetDBAByIdQuery,
  useDownloadLeadFileMutation,
} from "../store/api";
import { toast } from "react-toastify";
import { getErrorMessage } from "../utils/errorHandler";
// import { Typography } from '@mui/material';

const ViewMerchant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchDBAById] = useLazyGetDBAByIdQuery();
  const [downloadLeadFile] = useDownloadLeadFileMutation();
  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (file) => {
    const fileName = file.title || file.file_name || "download";
    const filePath = file.path;

    if (!filePath) {
      toast.error("No file available for download");
      return;
    }

    try {
      setDownloadingId(file.id);
      const res = await downloadLeadFile(filePath).unwrap();

      const blob =
        res instanceof Blob
          ? res
          : new Blob([res?.data ?? res], {
              type: res?.headers?.["content-type"] || "application/octet-stream",
            });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`${fileName} downloaded`);
    } catch (err) {
      toast.error(err?.data?.message || getErrorMessage(err) || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };
  // Get DBA ID from URL params or passed state
  const dbaId =
    new URLSearchParams(location.search).get("id") ||
    location.state?.merchantData?.id;

  useEffect(() => {
    const fetchMerchantData = async () => {
      if (!dbaId) {
        setError("No DBA ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetchDBAById(dbaId).unwrap();

        // Handle API response structure
        if (response && response.success) {
          if (response.data) {
            // Flatten the attribute_types structure into a simple attributes array
            const flattenedAttributes = [];
            if (
              response.data.attribute_types &&
              Array.isArray(response.data.attribute_types)
            ) {
              response.data.attribute_types.forEach((attributeType) => {
                if (
                  attributeType.attributes &&
                  Array.isArray(attributeType.attributes)
                ) {
                  attributeType.attributes.forEach((attribute) => {
                    flattenedAttributes.push({
                      ...attribute,
                      attribute_type_name: attributeType.name,
                      attribute_type_id: attributeType.id,
                    });
                  });
                }
              });
            }

            // Set the merchant data with flattened attributes
            setMerchantData({
              ...response.data,
              attributes: flattenedAttributes,
            });
          } else {
            setError("No data in response");
          }
        } else {
          setError(
            response?.message || "Merchant data not found - API call failed",
          );
        }
      } catch (err) {
        setError(err?.data?.message || getErrorMessage(err) || "Failed to fetch merchant data");
      } finally {
        setLoading(false);
      }
    };

    fetchMerchantData();
  }, [dbaId, fetchDBAById]);

  if (loading) {
    return (
      <PageLayout>
        <Container
          maxWidth="xl"
          sx={{
            py: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} />
        </Container>
      </PageLayout>
    );
  }

  if (error || !merchantData) {
    return (
      <PageLayout>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "No merchant data found. Please go back and try again."}
          </Alert>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Back
            </Typography>
          </IconButton>
        </Container>
      </PageLayout>
    );
  }

  // const sections = [
  //   {
  //     key: "merchantWorkflow",
  //     title: "MERCHANT WORKFLOW",
  //     items: [
  //       { key: "createMerchant", label: "New Merchant Applications", count: 3 },
  //       { key: "inUnderwriting", label: "Underwriting Applications", count: 3 },
  //       { key: "pending", label: "Pending Applications", count: 2 },
  //       { key: "completed", label: "Approved Accounts", count: 1 },
  //     ],
  //   },
  // ];

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const InfoCard = ({ title, icon, children, color = "primary" }) => (
    <Card sx={{ height: "auto" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              bgcolor: `${color}.main`,
              color: "white",
              borderRadius: 1,
              p: 1,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <PageLayout>
      {/* <Sidebar
        sections={sections}
        onItemClick={(key) => {
          switch (key) {
            case "createMerchant":
              navigate("/create-merchant");
              break;
            case "inUnderwriting":
              navigate("/in-underwriting");
              break;
            case "pending":
              navigate("/pending");
              break;
            case "completed":
              navigate("/complete");
              break;
            default:
          }
        }}
      /> */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton
            onClick={() => navigate("/in-underwriting")}
            sx={{ mr: 2, color: "#154A8A" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ color: "#154A8A", fontWeight: 600 }}>
            Merchant Details
          </Typography>
        </Box>

        {/* DBA Information */}
        <InfoCard
          title="DBA Information"
          icon={<BusinessIcon />}
          color="primary"
          sx={{ mb: 3 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                DBA Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.email || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.phone || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Fax Phone
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.fax_phone || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                SSN Number
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.ssn || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Address
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.address || "N/A"}, {merchantData.city || "N/A"},{" "}
                {merchantData.zip || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Submission date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.submission_date
                  ? formatDate(merchantData.submission_date)
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Monthly volume
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.monthly_volume || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Monthly lease
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.monthly_lease || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Is bundled
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.is_bundled === true ||
                merchantData.is_bundled === "Yes"
                  ? "Yes"
                  : merchantData.is_bundled === false ||
                      merchantData.is_bundled === "No"
                    ? "No"
                    : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Processing offered
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.processing_offered || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Highest ticket
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.highest_ticket || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                FNS number for EBT
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {merchantData.fns_number_for_ebt || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {formatDate(merchantData.created_at)}
              </Typography>
            </Grid>
          </Grid>
        </InfoCard>

        {/* Merchant Business Information */}
        {merchantData.merchant && (
          <InfoCard
            title="Business Information"
            icon={<BusinessIcon />}
            color="success"
            sx={{ mb: 3 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Federal Tax ID
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.federal_tax_id || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Number of Locations
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.no_locations || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Business Years
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.business_years || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Business Months
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.business_months || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Business Category
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.business_category || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Business Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.business_type || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Average Monthly Sales
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  ${merchantData.merchant.avg_month_sales || "0"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Return Policy
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.return_policy || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Delivery Days
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.delivery_days || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Fees Category
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.merchant.fees_category || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </InfoCard>
        )}

        {/* Banking Information */}
        {merchantData.bank_account && (
          <InfoCard
            title="Banking Information"
            icon={<BankIcon />}
            color="info"
            sx={{ mb: 3 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Bank Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.bank_name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.full_name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Account Owner
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.account_owner || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Business Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.bussiness_name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Account Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.account_type || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Routing Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.routing_number || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Account Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.account_number
                    ? "****" +
                      merchantData.bank_account.account_number.slice(-4)
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Fee Deduct Other
                </Typography>
                <Chip
                  label={
                    merchantData.bank_account.fee_deduct_other ? "Yes" : "No"
                  }
                  color={
                    merchantData.bank_account.fee_deduct_other
                      ? "success"
                      : "default"
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Card Present Transaction %
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.present_transaction || "0"}%
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  MOTO Transaction %
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.moto_transaction || "0"}%
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  E-commerce Transaction %
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {merchantData.bank_account.ecom_transaction || "0"}%
                </Typography>
              </Grid>
            </Grid>
          </InfoCard>
        )}

        {/* DBA Owners */}
        {merchantData.dba_Owners && merchantData.dba_Owners.length > 0 && (
          <InfoCard
            title="DBA Owners"
            icon={<PeopleIcon />}
            color="warning"
            sx={{ mb: 3 }}
          >
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ownership %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Manager</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Authorized</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchantData.dba_Owners.map((owner) => (
                    <TableRow key={owner.id}>
                      <TableCell>{owner.full_name || "N/A"}</TableCell>
                      <TableCell>{owner.email || "N/A"}</TableCell>
                      <TableCell>{owner.number || "N/A"}</TableCell>
                      <TableCell>
                        {owner.ownership_percentage || "0"}%
                      </TableCell>
                      <TableCell>{owner.designation || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={owner.is_manager ? "Yes" : "No"}
                          color={owner.is_manager ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={owner.is_authorized ? "Yes" : "No"}
                          color={owner.is_authorized ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        )}

        {/* Equipment */}
        {/* {merchantData.equipments && merchantData.equipments.length > 0 && (
            <InfoCard
              title="Equipment"
              icon={<EquipmentIcon />}
              color="secondary"
              sx={{ mb: 3 }}
            >
              <Grid container spacing={2}>
                {merchantData.equipments.map((equipment, index) => (
                  <Grid item xs={12} md={6} key={equipment.id || index}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {equipment.id || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Equipment Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {equipment.equipment_type?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manufacture Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {equipment.manufacturer?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Equipement Model Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        {equipment.equipment_model?.name || "N/A"}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </InfoCard>
          )} */}

        {merchantData.equipments && merchantData.equipments.length > 0 && (
          <InfoCard
            title="Equipment"
            icon={<EquipmentIcon />}
            color="secondary"
            sx={{ mb: 3 }}
          >
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Manufacturer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchantData.equipments.map((equipment, index) => (
                    <TableRow key={equipment.id || index}>
                      <TableCell>
                        {equipment.equipment_type?.name || "N/A"}
                      </TableCell>
                      <TableCell>
                        {equipment.manufacturer?.name || "N/A"}
                      </TableCell>
                      <TableCell>
                        {equipment.equipment_model?.name || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        )}

        {/* Attachment */}
        {merchantData.attachments && merchantData.attachments.length > 0 && (
          <InfoCard
            title="Attachments"
            icon={<PeopleIcon />}
            color="warning"
            sx={{ mb: 3 }}
          >
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchantData.attachments.map((owner) => (
                    <TableRow key={owner.id}>
                      <TableCell>
                        {owner.title
                          ? owner.title
                              .replace(/\.[^/.]+$/, "")
                              .replace(/[-_]/g, " ")
                          : "N/A"}
                        {owner?.type?.startsWith("image/") && owner?.path && (
                          <a
                            href={owner.path}
                            target="_blank"
                            rel="noopener noreferrer" // ← security best practice
                            style={{ display: "inline-block" }} // prevents layout shift
                          >
                            <Box
                              component="img"
                              src={owner.path}
                              alt="Owner / avatar / attachment"
                              width={40}
                              height={40}
                              sx={{
                                borderRadius: "50%",
                                objectFit: "cover",
                                display: "block",
                                transition: "opacity 0.2s", // optional nice touch
                                "&:hover": { opacity: 0.85 }, // visual feedback on hover
                                cursor: "pointer", // hand cursor
                              }}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.src = "/images/fallback.png";
                                e.currentTarget.onerror = null;
                              }}
                            />
                          </a>
                        )}
                      </TableCell>
                      <TableCell>{owner.type || "N/A"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {/* View Button */}
                          {/* {owner.path ? (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => window.open(owner.path, '_blank')}
                                title="View File"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No link
                              </Typography>
                            )} */}

                          {/* Download Button */}
                          {owner.path && (
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDownload(owner)}
                              title="Download File"
                              disabled={downloadingId === owner.id}
                            >
                              {downloadingId === owner.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        )}

        {/* Assigned Users */}
        {merchantData.users && merchantData.users.length > 0 && (
          <InfoCard
            title="Users"
            icon={<PeopleIcon />}
            color="info"
            sx={{ mb: 3 }}
          >
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Joining Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchantData.users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name || "N/A"}</TableCell>
                      <TableCell>{user.email || "N/A"}</TableCell>
                      <TableCell>{user.phone || "N/A"}</TableCell>
                      <TableCell>
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        )}

        {/* Attribute Values */}
        {/* {merchantData.attributes && merchantData.attributes.length > 0 && (
            <InfoCard
              title="Attribute Values"
              icon={<AttributesIcon />}
              color="primary"
            >
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Attribute Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {merchantData.attributes.map((attribute, index) => (
                      <TableRow key={attribute.id || index}>
                        <TableCell>
                          <Chip
                            label={attribute.attribute_type_name || "N/A"}
                            color="secondary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{attribute.content || "N/A"}</TableCell>
                        <TableCell>
                          {attribute.type === "checkbox" ? (
                            <Chip
                              label={
                                attribute.attribute_value === "1" ? "Yes" : "No"
                              }
                              color={
                                attribute.attribute_value === "1"
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                            />
                          ) : attribute.type === "select" ? (
                            <Typography variant="body2">
                              {attribute.attribute_value || "N/A"}
                            </Typography>
                          ) : attribute.type === "number" ? (
                            <Typography variant="body2">
                              {attribute.attribute_value
                                ? `${attribute.attribute_value}`
                                : "N/A"}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {attribute.attribute_value || "N/A"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={attribute.type || "text"}
                            color="info"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(attribute.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfoCard>
          )} */}
        {/* Attribute Values */}
        {merchantData.attributes &&
          merchantData.attributes.filter((attribute) => {
            // Filter out attributes with no value
            if (!attribute.attribute_value) return false;

            // For checkbox type, only show "Yes" values
            if (
              attribute.type === "checkbox" &&
              attribute.attribute_value !== "1"
            )
              return false;

            // For other types, show if there's any value
            return true;
          }).length > 0 && (
            <InfoCard
              title="Attribute Values"
              icon={<AttributesIcon />}
              color="primary"
            >
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Attribute Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {merchantData.attributes
                      .filter((attribute) => {
                        // Filter out attributes with no value
                        if (!attribute.attribute_value) return false;

                        // For checkbox type, only show "Yes" values
                        if (
                          attribute.type === "checkbox" &&
                          attribute.attribute_value !== "1"
                        )
                          return false;

                        // For other types, show if there's any value
                        return true;
                      })
                      .map((attribute, index) => (
                        <TableRow key={attribute.id || index}>
                          <TableCell>
                            <Chip
                              label={attribute.attribute_type_name || "N/A"}
                              color="secondary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{attribute.content || "N/A"}</TableCell>
                          <TableCell>
                            {attribute.type === "checkbox" ? (
                              <Chip label="Yes" color="success" size="small" />
                            ) : attribute.type === "select" ? (
                              <Typography variant="body2">
                                {attribute.attribute_value || "N/A"}
                              </Typography>
                            ) : attribute.type === "number" ? (
                              <Typography variant="body2">
                                {attribute.attribute_value
                                  ? `${attribute.attribute_value}`
                                  : "N/A"}
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {attribute.attribute_value || "N/A"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={attribute.type || "text"}
                              color="info"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(attribute.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfoCard>
          )}
      </Container>
    </PageLayout>
  );
};

export default ViewMerchant;
